import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/CategoryBadge";
import { useEntities, useInvalidate, useQuoteTags } from "@/lib/queries";
import { TagChip } from "@/components/quotes/TagInput";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { QuoteCard } from "@/components/quotes/QuoteCard";
import { QuoteEditor } from "@/components/quotes/QuoteEditor";
import { AssignSpeaker } from "@/components/quotes/AssignSpeaker";
import { SpeakerSelect } from "@/components/quotes/SpeakerSelect";
import { ShareQuoteDialog } from "@/components/quotes/ShareQuoteDialog";

export const useQuoteActions = (uid) => {
  const invalidate = useInvalidate();
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [sharing, setSharing] = useState(null);
  const refresh = () => invalidate(["quotes", uid], "entity-quotes", ["entities", uid]);
  const dialogs = (entities, defaultSpeaker) => (
    <>
      {editing && <QuoteEditor uid={uid} quote={editing.id ? editing : null} entities={entities} defaultSpeaker={defaultSpeaker} onClose={() => setEditing(null)} onSaved={refresh} />}
      {sharing && <ShareQuoteDialog quote={sharing} uid={uid} onClose={() => setSharing(null)} />}
      {assigning && <AssignSpeaker quote={assigning} entities={entities} onClose={() => setAssigning(null)} onSaved={refresh} />}
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title="Delete this quote?" description="It will be removed from the library and from every speaker's entry."
        onConfirm={async () => { await api.delete(`/quotes/${deleting.id}`); toast.success("Quote deleted"); refresh(); setDeleting(null); }} />
    </>
  );
  return { onShare: setSharing, onEdit: setEditing, onAssign: setAssigning, onDelete: setDeleting, add: () => setEditing({}), dialogs };
};

export default function QuotesPage() {
  const { universe, canEdit, openEntity } = useOutletContext();
  const { data: entities = [] } = useEntities(universe.id);
  const [q, setQ] = useState("");
  const [speaker, setSpeaker] = useState("all");
  const [tags, setTags] = useState([]);
  const toggleTag = (t) => setTags((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));
  const { data: allTags = [] } = useQuoteTags(universe.id);
  const params = new URLSearchParams({ ...(q ? { q } : {}), ...(speaker !== "all" ? { speaker: speaker.replace("link:", "") } : {}), ...(tags.length ? { tags: tags.join(",") } : {}) }).toString();
  const { data: quotes = [], isLoading } = useQuery({ queryKey: ["quotes", universe.id, params], queryFn: () => api.get(`/universes/${universe.id}/quotes?${params}`).then((r) => r.data) });
  const actions = useQuoteActions(universe.id);

  return (
    <div className="px-4 py-10 sm:px-8 lg:px-12" data-testid="quotes-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <PageHeader eyebrow="Repository" title="Quote Library" />
        {canEdit && <Button onClick={actions.add} className="mb-8" data-testid="quote-add-button"><Plus className="mr-1.5 h-4 w-4" />Add quote</Button>}
      </div>
      <div className="mb-8 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A8A090]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search words, speakers, context" className="pl-9" data-testid="quote-search-input" />
        </div>
        <SpeakerSelect entities={entities} value={speaker} onChange={setSpeaker} testid="quote-speaker-filter" className="h-10 sm:w-64"
          extra={[{ value: "all", label: "All speakers" }, { value: "unattributed", label: "Unattributed" }]} />
      </div>
      {allTags.length > 0 && (
        <div className="-mt-4 mb-8 flex flex-wrap items-center gap-1.5" data-testid="quote-tag-filter">
          <span className="mr-1 text-xs text-[#A8A090]">Tags</span>
          {allTags.map(({ tag, count }) => <TagChip key={tag} tag={tag} count={count} active={tags.includes(tag)} onClick={() => toggleTag(tag)} testid={`quote-tag-filter-${tag}`} />)}
          {tags.length > 0 && <button onClick={() => setTags([])} className="ml-1 text-xs text-[#F59E0B] hover:underline" data-testid="quote-tag-filter-clear">Clear</button>}
        </div>
      )}
      {isLoading ? <p className="text-[#A8A090]">Loading quotes…</p> : quotes.length === 0 ? (
        <div className="rounded-md border border-dashed border-[#2D3442] p-10" data-testid="quotes-empty">
          <p className="font-display text-3xl text-[#F3EBDD]">{q || speaker !== "all" || tags.length ? "No quotes match." : "No quotes yet."}</p>
          <p className="mt-2 max-w-md text-sm text-[#A8A090]">On the Notes page, choose "Quote" as the type (or let Auto-detect spot it) and the scribe will file it here and on each speaker's entry.</p>
        </div>
      ) : (
        <div className="columns-1 gap-5 lg:columns-2 [&>*]:mb-5 [&>*]:break-inside-avoid" data-testid="quotes-list">
          {quotes.map((x) => <QuoteCard key={x.id} q={x} onNavigate={openEntity} canEdit={canEdit} onTag={toggleTag} activeTags={tags} {...actions} />)}
        </div>
      )}
      {actions.dialogs(entities)}
    </div>
  );
}
